import React, { useCallback, useEffect, useState } from 'react'
import { useGetAccountLoginBulk } from 'modules/Forms/Account/Create/subcomponents/LoginField/useGetAccountLoginBulk'
import styles from 'modules/Forms/Account/Create/subcomponents/LoginField/multilogin.module.scss'
import { Button, Grid, Icon, TextInput, Tooltip, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import { arrayInsert, change, Field } from 'redux-form'
import renderInputField from 'components/FormControls/textInput'
import GridSpinner from 'components/UI/GridSpinner'
import { useTranslation } from 'react-i18next'
import isObject from 'lodash/isObject'
import { useDispatch } from 'react-redux'
import formStyles from 'modules/Forms/common-form.module.scss'

const DEFAULT_LOGINS_LENGTH = 3
const LOGINS_MAX_QUANTITY = 10

const newRowTemplate = {
  login: undefined,
  loading: true,
  disabled: true,
  postfix: '',
  prefix: '',
  custom: false,
}

const LoginRow = ({ fields, networkId, label, array, form, meta: { submitting } }) => {
  const [inputValue, setInputValue] = useState(DEFAULT_LOGINS_LENGTH)
  const { getAccountLogins } = useGetAccountLoginBulk(networkId)
  const [lock, setLock] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const updateLogins = useCallback(
    (number) => {
      if (lock) return

      const newFieldsLength = fields
        .getAll()
        .filter(({ created = null }) => created === null).length

      if (!newFieldsLength) return

      setLock(true)
      return (async () => {
        const results = await getAccountLogins(newFieldsLength)

        const lastFieldIndex = fields.length - results.length

        results.map((res, i) => {
          const row = fields.get(lastFieldIndex + i)
          if (!row || row?.custom || Object.hasOwn(row, 'created')) return

          dispatch(
            change(
              form,
              `${fields.name}[${lastFieldIndex + i}]`,
              {
                ...row,
                ...res,
                loading: false,
                login: res.prefix + res.postfix,
              },
              false,
              false,
            ),
          )
        })

        setLock(false)
      })(number)
    },
    [fields, getAccountLogins, form, dispatch, lock],
  )

  useEffect(() => {
    if (!networkId) return

    for (let i = 0; i < DEFAULT_LOGINS_LENGTH; i++) {
      dispatch(
        change(
          form,
          `${fields.name}[${i}]`,
          {
            ...newRowTemplate,
            login: newRowTemplate.prefix + newRowTemplate.postfix,
            index: i,
          },
          false,
          false,
        ),
      )
    }
  }, [networkId])

  const setRows = useCallback(
    (newQuantity) => {
      if (lock) return

      setLock(true)
      const delta = fields.length - newQuantity

      if (delta > 0) {
        for (let i = fields.length; i > newQuantity; i--) {
          array.remove(`${fields.name}`, i - 1)
        }
      }

      if (delta < 0) {
        for (let i = fields.length; i < newQuantity; i++) {
          dispatch(arrayInsert(form, fields.name, i, { ...newRowTemplate, index: i }, false))
        }
      }
      setLock(false)
    },
    [fields, lock, setLock],
  )

  useEffect(() => {
    if (fields.length) {
      setInputValue(fields.length)
      updateLogins(fields.length)
    }
  }, [fields.length])

  return (
    <div className={styles['container']}>
      <Grid
        gap={8}
        className={'margin-bottom-16'}
        alignItems={'center'}
      >
        <Typography
          variant={'Subtitle-20'}
          component={'h3'}
        >
          {label}
        </Typography>
        <Tooltip
          title={t('services::forms.register_account.yandex.fields.login.tooltip')}
          placement="top"
          arrow
          tooltipClassName={formStyles['tooltip']}
        >
          <Icon
            name={'information'}
            color={'Label-Secondary'}
            size={'m'}
          />
        </Tooltip>
      </Grid>
      {fields.length === 0 && (
        <div className={'margin-bottom-16'}>
          <GridSpinner width={24} />
        </div>
      )}
      {fields.length !== 0 && (
        <Grid
          display={'flex'}
          flexDirection={'row'}
          wrap={'nowrap'}
          gap={8}
          className={'margin-bottom-16'}
        >
          <Button
            type={'button'}
            size={'l'}
            noLabel
            startIcon={
              <Icon
                name="minus"
                size={'m'}
                color={'Interface-Eighth'}
              />
            }
            variant={'secondary'}
            onClick={() => setRows(Math.max(1, fields.length - 1))}
            borderColor={'Interface-Quaternary'}
            disabled={fields.length - 1 < 1 || lock || submitting}
          />

          <TextInput
            className={styles['count-input']}
            label={t('services::forms.shared_fields.logins.quantity')}
            type={'number'}
            value={inputValue}
            disabled={lock || submitting}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            onBlur={(e) =>
              setRows(
                Number(e.currentTarget.value) > LOGINS_MAX_QUANTITY
                  ? LOGINS_MAX_QUANTITY
                  : Number(e.currentTarget.value) === 0
                  ? 1
                  : Number(e.currentTarget.value),
              )
            }
          />

          <Button
            type={'button'}
            disabled={fields.length + 1 > LOGINS_MAX_QUANTITY || lock || submitting}
            size={'l'}
            noLabel
            startIcon={
              <Icon
                name="plus"
                size={'m'}
                color={'Interface-Eighth'}
              />
            }
            variant={'secondary'}
            onClick={() => setRows(Math.min(fields.length + 1, LOGINS_MAX_QUANTITY))}
            borderColor={'Interface-Quaternary'}
          />
        </Grid>
      )}
      {fields.map((member, i, fields) => {
        const row = fields.get(i)
        return !isObject(row) ? null : (
          <div
            className={'margin-bottom-16'}
            key={'login-' + i}
          >
            <Field
              fullWidth
              t={t}
              name={member}
              prefix={row.prefix}
              normalize={(value) => ({
                ...row,
                postfix: value ?? '',
                login: row.prefix + value,
              })}
              format={(value) => value.postfix}
              disabled={row.disabled}
              readOnly={row.created}
              clearButton={row.created === null && !submitting}
              label={t('services::forms.register_account.yandex.fields.login.label')}
              component={renderInputField}
              onKeyDown={() =>
                dispatch(
                  change(
                    form,
                    member,
                    {
                      ...row,
                      custom: true,
                      created: row.created ? true : null,
                    },
                    false,
                    false,
                  ),
                )
              }
              InputProps={{
                startAdornment: row.loading && <GridSpinner width={24} />,
                endAdornment:
                  submitting && !row.created ? (
                    <GridSpinner
                      width={24}
                      color={'Interface-GU-Primary'}
                    />
                  ) : typeof row.created === 'boolean' ? (
                    <Icon
                      name={row.created ? 'check' : 'status warning'}
                      color={row.created ? 'Accent-Confirm-Primary' : 'Accent-Error-Primary'}
                      size={'sm'}
                    />
                  ) : null,
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

export default LoginRow

LoginRow.propTypes = {
  fields: PropTypes.object,
  networkId: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  form: PropTypes.string,
  meta: PropTypes.object,
  array: PropTypes.object,
}
